package dev.lingvo.site.state

import dev.lingvo.shared.OverviewResponse
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ProjectSummaryDto
import dev.lingvo.shared.Role
import dev.lingvo.shared.ServerFeaturesDto
import dev.lingvo.shared.WorkLogEntryDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.shared.atLeast
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionRequestIdentity
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

internal enum class WorkspaceRequestKind {
    PROJECT_LIST,
    OVERVIEW,
    ACTIVITY,
    DISMISS_ATTENTION,
    CREATE_PROJECT,
    SAMPLE_PROJECT,
}

internal data class WorkspaceRequestDecision(
    val orgSlug: String?,
    val refusal: String? = null,
) {
    val allowed: Boolean get() = orgSlug != null && refusal == null
}

internal data class WorkspaceRequestKey(
    val session: SessionRequestIdentity,
    val workspaceId: String,
    val workspaceSlug: String,
)

/**
 * The one selected-workspace decision used by every authenticated workspace request.
 *
 * It is deliberately pure: feature negotiation, legacy compatibility, role, filtering, and
 * provenance checks can be reviewed without a browser or transport. Callers receive either the
 * exact slug they may send or a refusal; there is no "try it and see where the old server writes"
 * branch.
 */
internal data class WorkspaceRequestContext(
    val session: SessionRequestIdentity,
    val workspace: WorkspaceDto,
    val workspaceCount: Int,
    val serverFeatures: ServerFeaturesDto,
) {
    val key = WorkspaceRequestKey(session, workspace.id, workspace.slug)
    val role: Role get() = workspace.role
    val canManageProjects: Boolean get() = role.atLeast(Role.ADMIN)
    val canManageAgents: Boolean get() = role.atLeast(Role.ADMIN)

    fun decision(kind: WorkspaceRequestKind): WorkspaceRequestDecision {
        val supported = workspaceCount == 1 || when (kind) {
            WorkspaceRequestKind.PROJECT_LIST -> true
            WorkspaceRequestKind.OVERVIEW,
            WorkspaceRequestKind.DISMISS_ATTENTION,
            -> serverFeatures.workspaceScopedOverview
            WorkspaceRequestKind.ACTIVITY -> serverFeatures.workspaceScopedActivity
            WorkspaceRequestKind.CREATE_PROJECT -> serverFeatures.explicitProjectWorkspace
            WorkspaceRequestKind.SAMPLE_PROJECT -> serverFeatures.explicitSampleWorkspace
        }
        return if (supported) {
            WorkspaceRequestDecision(orgSlug = workspace.slug)
        } else {
            WorkspaceRequestDecision(
                orgSlug = null,
                refusal = tr("state.workspace_request_context.this_server_cannot_safely_separate_this", "This server cannot safely separate this action between multiple workspaces yet."),
            )
        }
    }

    fun filterProjects(projects: List<ProjectSummaryDto>): List<ProjectSummaryDto> =
        projects.mapNotNull(::normalizeProject)

    fun agentProjectAllowlist(projects: List<ProjectSummaryDto>): List<ProjectSummaryDto> =
        filterProjects(projects)

    fun acceptsProject(project: ProjectSummaryDto): Boolean =
        project.orgId == workspace.id && project.orgSlug.isBlankOrEqualTo(workspace.slug)

    fun acceptsProject(project: ProjectDetailDto): Boolean =
        project.orgId == workspace.id && project.orgSlug.isBlankOrEqualTo(workspace.slug)

    fun normalizeProject(project: ProjectSummaryDto): ProjectSummaryDto? =
        project.takeIf(::acceptsProject)?.let {
            if (it.orgSlug.isBlank()) it.copy(orgSlug = workspace.slug) else it
        }

    fun normalizeProject(project: ProjectDetailDto): ProjectDetailDto? =
        project.takeIf(::acceptsProject)?.let {
            if (it.orgSlug.isBlank()) it.copy(orgSlug = workspace.slug) else it
        }

    fun acceptsOverview(response: OverviewResponse): Boolean {
        if (!response.hasWorkspace) return false
        val rootMatches = if (serverFeatures.workspaceScopedOverview) {
            response.workspaceSlug == workspace.slug
        } else {
            workspaceCount == 1 && (response.workspaceSlug == null || response.workspaceSlug == workspace.slug)
        }
        return rootMatches && response.workLog.all(::acceptsWorkLogRow)
    }

    fun acceptsActivity(entries: List<WorkLogEntryDto>): Boolean {
        if (serverFeatures.workspaceScopedActivity) return entries.all(::acceptsNegotiatedWorkLogRow)
        if (workspaceCount != 1) return false
        return entries.all { row ->
            (row.workspaceSlug == null || row.workspaceSlug == workspace.slug) &&
                (row.projectSlug == null || row.href.namesProject(row.projectSlug))
        }
    }

    fun createdProjectRoute(project: ProjectDetailDto): String? =
        project.takeIf(::acceptsProject)?.let { "/app/projects/${it.slug}" }

    private fun acceptsWorkLogRow(row: WorkLogEntryDto): Boolean =
        if (serverFeatures.workspaceScopedOverview) {
            acceptsNegotiatedWorkLogRow(row)
        } else {
            workspaceCount == 1 &&
                (row.workspaceSlug == null || row.workspaceSlug == workspace.slug) &&
                (row.projectSlug == null || row.href.namesProject(row.projectSlug))
        }

    private fun acceptsNegotiatedWorkLogRow(row: WorkLogEntryDto): Boolean =
        row.workspaceSlug == workspace.slug &&
            !row.projectSlug.isNullOrBlank() &&
            row.href.namesProject(row.projectSlug)

    companion object {
        fun current(): WorkspaceRequestContext? = resolve(
            session = Session.requestIdentity ?: return null,
            memberships = Session.organizations,
            selectedSlug = WorkspaceSelection.current()?.slug,
            serverFeatures = Session.serverFeatures,
        )

        fun resolve(
            session: SessionRequestIdentity,
            memberships: List<WorkspaceDto>,
            selectedSlug: String?,
            serverFeatures: ServerFeaturesDto,
        ): WorkspaceRequestContext? {
            val selected = if (selectedSlug == null) {
                memberships.minByOrNull { it.role.ordinal }
            } else {
                memberships.firstOrNull { it.slug == selectedSlug }
            } ?: return null
            return WorkspaceRequestContext(
                session = session,
                workspace = selected,
                workspaceCount = memberships.size,
                serverFeatures = serverFeatures,
            )
        }
    }
}

internal data class WorkspaceRetainedResult<T>(
    val key: WorkspaceRequestKey,
    val value: T? = null,
    val loading: Boolean = true,
    val error: String? = null,
    val requestGeneration: Long = 1,
)

internal fun <T> beginWorkspaceRequest(
    previous: WorkspaceRetainedResult<T>?,
    key: WorkspaceRequestKey,
): WorkspaceRetainedResult<T> = if (previous?.key == key) {
    previous.copy(
        loading = true,
        error = null,
        requestGeneration = previous.requestGeneration + 1,
    )
} else {
    WorkspaceRetainedResult(key = key)
}

internal fun <T> completeWorkspaceRequest(
    current: WorkspaceRetainedResult<T>,
    key: WorkspaceRequestKey,
    result: Result<T>,
    accepts: (T) -> Boolean,
    requestGeneration: Long = current.requestGeneration,
): WorkspaceRetainedResult<T> {
    if (current.key != key || current.requestGeneration != requestGeneration) return current
    return result.fold(
        onSuccess = { candidate ->
            if (accepts(candidate)) {
                current.copy(value = candidate, loading = false, error = null)
            } else {
                current.copy(
                    loading = false,
                    error = tr("state.workspace_request_context.the_server_returned_data_from_a", "The server returned data from a different workspace. The previous verified view is unchanged."),
                )
            }
        },
        onFailure = { failure ->
            current.copy(
                loading = false,
                error = failure.message?.takeIf(String::isNotBlank) ?: "Couldn't load this workspace.",
            )
        },
    )
}

private fun String.isBlankOrEqualTo(expected: String): Boolean = isBlank() || this == expected

private fun String.namesProject(projectSlug: String?): Boolean {
    if (projectSlug.isNullOrBlank()) return false
    val base = "/app/projects/$projectSlug"
    return this == base || startsWith("$base/") || startsWith("$base?")
}
